import { useEffect, useRef, useCallback, useState } from 'react';
import { gsap } from 'gsap';
import './DadBirthday.css';

// --- Content data ---
const sections = [
  { type: 'counter' },
  { type: 'divider' },

  // Opening — big impact
  { type: 'A', src: '/birthday/balloon.jpg', caption: 'To all the unreal adventures you have instigated', objectPosition: 'center bottom' },
  { type: 'C', photos: [
    { src: '/birthday/family-portrait.jpg', caption: 'The unwavering support' },
    { src: '/birthday/dad-portrait.jpeg', caption: 'The privelage to experience' },
  ]},
  { type: 'B', src: '/birthday/dad-smiling-bar.jpeg', title: 'Infectious Zest for Life', text: 'That smile says it all' },

  { type: 'quote', text: 'Turns out the first 60 years just a warmup!' },

  // Adventures
  { type: 'A', src: '/birthday/hawaii.jpg', caption: 'Paradise found', objectPosition: 'center bottom' },
  { type: 'C', photos: [
    { src: '/birthday/skiing.jpeg', caption: 'Thank god you taught us to ski!' },
    { src: '/birthday/golf-ocean.jpeg', caption: 'No Brim?!' },
  ]},

  { type: 'divider' },

  // Celebrations
  { type: 'B', src: '/birthday/christmas.jpeg', title: 'Christmas morning', text: 'Some traditions never get old... No matter the sickness', reverse: true },
  { type: 'A', src: '/birthday/formal-dinner.jpeg', caption: 'Us on your leash!' },
  { type: 'C', photos: [
    { src: '/birthday/whiskey-tasting.jpeg', caption: 'Passion for alcohol' },
    { src: '/birthday/wine-tasting.jpg', caption: 'Never enough fun' },
  ]},

  { type: 'quote', text: 'To teaching us that life is too short for bad wine and boring weekends' },

  // Family
  { type: 'A', src: '/birthday/nighttime-family.jpg', caption: 'Late nights, good vibes', objectPosition: 'center bottom' },
  { type: 'B', src: '/birthday/class-2025.jpeg', title: 'Class of 2025', text: 'A proud moment for everyone.' },
  { type: 'C', photos: [
    { src: '/birthday/concert.jpg', caption: 'Music included!' },
    { src: '/birthday/uma-uma.jpeg', caption: 'The whole gang' },
  ]},

  { type: 'divider' },

  // Lessons
  { type: 'lessons-title' },
  { type: 'lesson', text: 'Work smart and hard but never forget to love the life you are living.' },
  { type: 'lesson', text: 'Show up for the people you love. Better yet, be the reason they are all in the same room.' },
  { type: 'lesson', text: 'Never stop being curious and zealous for life! Turns out there are well over 60 years of things to do.' },

  { type: 'divider' },

  // Fun + closing photos
  { type: 'C', photos: [
    { src: '/birthday/penguin-cutout.jpeg', caption: 'Nailed it 🐧' },
    { src: '/birthday/under-table.jpg', caption: 'And a classic' },
  ]},
  { type: 'B', src: '/birthday/grilling.jpg', title: 'Grill master', text: 'The undisputed king of the backyard.' },
  { type: 'A', src: '/birthday/casual-wine.jpg', caption: 'To many more adventures!' },

  { type: 'divider' },

  // Video
  { type: 'video' },
];

// --- Scroll reveal ---
function setupScrollReveal(container) {
  const selectors = '.reveal, .reveal-stagger-left, .reveal-stagger-right';
  const elements = container.querySelectorAll(selectors);
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    },
    { root: container, threshold: 0.08 }
  );
  elements.forEach((el) => observer.observe(el));
  return () => observer.disconnect();
}

// --- Ken Burns ---
function setupKenBurns(container) {
  const photos = container.querySelectorAll('.photo-full img');
  if (!photos.length) return () => {};

  const tweens = new Map();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const img = entry.target;
        if (entry.isIntersecting) {
          img.classList.add('ken-burns');
          const tween = gsap.fromTo(img,
            { scale: 1.0 },
            { scale: 1.06, duration: 10, ease: 'none' }
          );
          tweens.set(img, tween);
        } else {
          const tween = tweens.get(img);
          if (tween) { tween.kill(); tweens.delete(img); }
          img.classList.remove('ken-burns');
          gsap.set(img, { scale: 1 });
        }
      });
    },
    { root: container, threshold: 0.1 }
  );

  photos.forEach((img) => observer.observe(img));
  return () => {
    observer.disconnect();
    tweens.forEach((t) => t.kill());
  };
}

// --- Parallax ---
function setupParallax(container) {
  const photos = container.querySelectorAll('.photo-parallax img');
  if (!photos.length) return () => {};

  const handler = () => {
    photos.forEach((img) => {
      const parent = img.closest('.photo-parallax');
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
      const offset = (rect.top / window.innerHeight - 0.5) * -25;
      // Don't override Ken Burns scale — add to it
      const currentScale = img.classList.contains('ken-burns') ? '' : 'scale(1.06)';
      img.style.transform = `translateY(${offset}px) ${currentScale}`;
    });
  };

  container.addEventListener('scroll', handler, { passive: true });
  return () => container.removeEventListener('scroll', handler);
}

// --- Progress bar ---
function setupProgressBar(container, barEl) {
  if (!barEl) return () => {};
  const handler = () => {
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight - container.clientHeight;
    barEl.style.width = `${scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0}%`;
  };
  container.addEventListener('scroll', handler, { passive: true });
  return () => container.removeEventListener('scroll', handler);
}

// --- 60 counter ---
function setupCounter(container, counterEl) {
  if (!counterEl) return () => {};
  let animated = false;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animated) {
          animated = true;
          const obj = { val: 0 };
          gsap.to(obj, {
            val: 60, duration: 2.5, ease: 'power2.out',
            onUpdate: () => { counterEl.textContent = Math.round(obj.val); },
          });
        }
      });
    },
    { root: container, threshold: 0.3 }
  );
  observer.observe(counterEl);
  return () => observer.disconnect();
}

// --- Cursor particles ---
function setupCursorParticles(container) {
  let lastTime = 0;
  const handler = (e) => {
    const now = Date.now();
    if (now - lastTime < 60) return;
    lastTime = now;
    for (let i = 0; i < 2; i++) {
      const el = document.createElement('div');
      const size = 1.5 + Math.random() * 3;
      el.style.cssText = `
        position: fixed;
        width: ${size}px; height: ${size}px;
        border-radius: 50%;
        background: rgba(212, 168, 83, ${0.25 + Math.random() * 0.35});
        box-shadow: 0 0 ${size}px rgba(212, 168, 83, 0.2);
        pointer-events: none; z-index: 5003;
        left: ${e.clientX + (Math.random() - 0.5) * 14}px;
        top: ${e.clientY + (Math.random() - 0.5) * 14}px;
      `;
      container.appendChild(el);
      gsap.to(el, {
        y: -(8 + Math.random() * 16),
        x: (Math.random() - 0.5) * 16,
        opacity: 0, scale: 0,
        duration: 0.4 + Math.random() * 0.3,
        ease: 'power2.out',
        onComplete: () => el.remove(),
      });
    }
  };
  window.addEventListener('mousemove', handler);
  return () => window.removeEventListener('mousemove', handler);
}

// --- Auto-confetti on closing ---
function setupAutoConfetti(container, closingEl) {
  if (!closingEl) return () => {};
  let fired = false;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !fired) {
          fired = true;
          launchConfetti(container);
        }
      });
    },
    { root: container, threshold: 0.5 }
  );
  observer.observe(closingEl);
  return () => observer.disconnect();
}

// --- Confetti ---
function launchConfetti(container) {
  const count = 60;
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight * 0.4;
  const colors = ['#d4a853', '#f5c842', '#c0795a', '#5a8ab0', '#f5f0e8', '#8ac48a'];
  for (let i = 0; i < count; i++) {
    const el = document.createElement('div');
    const size = 4 + Math.random() * 8;
    const isRect = Math.random() > 0.4;
    el.style.cssText = `
      position: fixed; width: ${size}px; height: ${isRect ? size * 2.5 : size}px;
      border-radius: ${isRect ? '2px' : '50%'};
      background: ${colors[Math.floor(Math.random() * colors.length)]};
      pointer-events: none; z-index: 10000;
      left: ${cx}px; top: ${cy}px;
    `;
    container.appendChild(el);
    const angle = Math.random() * Math.PI * 2;
    const velocity = 150 + Math.random() * 450;
    gsap.to(el, {
      x: Math.cos(angle) * velocity,
      y: Math.sin(angle) * velocity - 200 + 500,
      rotation: Math.random() * 900 - 450,
      opacity: 0,
      duration: 1.5 + Math.random() * 1.5,
      ease: 'power2.out',
      onComplete: () => el.remove(),
    });
  }
}

export default function DadBirthday({ onBack }) {
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const counterRef = useRef(null);
  const closingRef = useRef(null);
  const titleRef = useRef(null);
  const [hiddenMsg] = useState(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cr, cp, cpb, cc, ck, ccp, cac;
    const raf = requestAnimationFrame(() => {
      cr = setupScrollReveal(container);
      cp = setupParallax(container);
      cpb = setupProgressBar(container, progressRef.current);
      cc = setupCounter(container, counterRef.current);
      ck = setupKenBurns(container);
      ccp = setupCursorParticles(container);
      cac = setupAutoConfetti(container, closingRef.current);
    });
    return () => {
      cancelAnimationFrame(raf);
      cr?.(); cp?.(); cpb?.(); cc?.(); ck?.(); ccp?.(); cac?.();
    };
  }, []);

  const handleTitleDblClick = useCallback(() => {
    if (!titleRef.current) return;
    const el = titleRef.current;
    gsap.to(el, {
      scale: 1.05, duration: 0.15, ease: 'power2.out',
      onComplete: () => {
        el.textContent = 'Best Dad Ever';
        gsap.to(el, { scale: 1, duration: 0.3, ease: 'elastic.out(1, 0.5)' });
        if (containerRef.current) launchConfetti(containerRef.current);
        setTimeout(() => { el.textContent = 'Happy Birthday, Dad!!!'; }, 3000);
      },
    });
  }, []);

  const handleConfetti = useCallback(() => {
    if (containerRef.current) launchConfetti(containerRef.current);
  }, []);

  const renderSection = (section, i) => {
    switch (section.type) {
      case 'counter':
        return (
          <div key={i} className="bday-counter-section reveal">
            <div className="bday-counter-num" ref={counterRef}>0</div>
            <div className="bday-counter-label">years of being the greatest</div>
          </div>
        );

      case 'divider':
        return <div key={i} className="bday-divider" />;

      case 'A':
        return (
          <div key={i} className="photo-full photo-parallax reveal">
            <img src={section.src} alt={section.caption || ''} loading="lazy" style={section.objectPosition ? { objectPosition: section.objectPosition } : undefined} />
            {section.caption && (
              <div className="photo-full-caption">
                <p>{section.caption}</p>
              </div>
            )}
          </div>
        );

      case 'B':
        return (
          <div key={i} className={`photo-side reveal ${section.reverse ? 'reverse' : ''}`}>
            <div className="photo-side-img">
              <img src={section.src} alt={section.title || ''} loading="lazy" />
            </div>
            <div className="photo-side-text">
              <h3>{section.title}</h3>
              <p>{section.text}</p>
            </div>
          </div>
        );

      case 'C':
        return (
          <div key={i} className="photo-pair photo-parallax">
            {section.photos.map((photo, j) => (
              <div key={j} className={`photo-pair-item ${j === 0 ? 'reveal-stagger-left' : 'reveal-stagger-right'}`}>
                <img src={photo.src} alt={photo.caption || ''} loading="lazy" style={photo.objectPosition ? { objectPosition: photo.objectPosition } : undefined} />
                {photo.caption && (
                  <div className="photo-pair-caption">
                    <p>{photo.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'quote':
        return (
          <div key={i} className="bday-quote reveal">
            <p>{section.text}</p>
            {section.attr && <span className="quote-attr">{section.attr}</span>}
          </div>
        );

      case 'lessons-title':
        return (
          <div key={i} className="bday-lessons-title reveal">
            <p>Just a few of the things I&#8217;ve learned from you</p>
          </div>
        );

      case 'lesson':
        return (
          <div key={i} className="bday-lesson-screen reveal">
            <div className="bday-lesson-text">{section.text}</div>
          </div>
        );

      case 'video':
        return (
          <div key={i} className="bday-video-section reveal">
            <div className="bday-video-wrapper">
              <video controls playsInline preload="metadata" className="bday-video">
                <source src="/birthday/video.mov" type="video/quicktime" />
                <source src="/birthday/video.mov" type="video/mp4" />
              </video>
              <p className="bday-video-caption">Jump for your birthday! 🎉</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bday bday-entrance" ref={containerRef} style={{ cursor: `url(${process.env.PUBLIC_URL}/birthday/dad-cursor.png) 48 48, auto` }}>
      <div className="bday-progress" ref={progressRef} />
      <button onClick={onBack} className="bday-back">← Back</button>
      <div className={`bday-hidden-msg ${hiddenMsg ? 'show' : ''}`}>{hiddenMsg}</div>

      <div className="bday-hero">
        <h1 ref={titleRef} onDoubleClick={handleTitleDblClick} title="Try double-clicking...">
          Happy Birthday, Dad!
        </h1>
        <p>A collection of moments</p>
        <div className="bday-scroll-hint">scroll</div>
      </div>

      {sections.map(renderSection)}

      <div className="bday-closing reveal" ref={closingRef}>
        <p>Thank you for everything, Dad.</p>
      </div>

      <div className="bday-confetti-section reveal">
        <button className="bday-confetti-btn" onClick={handleConfetti}>
          celebrate
        </button>
      </div>
    </div>
  );
}
